import { doc, onSnapshot, Timestamp } from "firebase/firestore";
import { useEffect, useRef } from "react";
import { useAppSelector } from "../storeHooks";
import { fireStore } from "../../firebase";
import { iUserDetails } from "../../types/userType";

/**
 * Fires when Baqaal bumps `updatedAt` on `vendors/{teamMemberId}`.
 *
 * The signal is deliberately generic — "something about you changed" — and is
 * written for **every** team member of a vendor, so the callback must be cheap
 * and idempotent. Do not chain anything heavy off it.
 *
 * Same collection and document id as `useOnUpdateUserNotifications`, watching a
 * different field. Kept as a separate listener rather than folded into that one:
 * routing terms refreshes through a field named `notificationsUpdated` would be
 * misleading, and would drag a pointless notifications refetch along with it.
 *
 * Differs from that hook in one way. It calls its callback from an effect keyed
 * on the stored value, which also fires on mount, before any snapshot. Here that
 * would mean a redundant GET every time the provider mounts — `onSnapshot`
 * delivers the current document immediately on subscribe — so the comparison is
 * done against a ref inside the handler instead, and the first delivery is
 * treated as the baseline.
 */
export const useOnVendorUpdated = (callback: () => void) => {
	const user = useAppSelector((state) => state.user?.userDetails as iUserDetails);

	// Kept in a ref so a caller passing an inline arrow does not resubscribe on
	// every render.
	const callbackRef = useRef(callback);
	callbackRef.current = callback;

	/** Last `updatedAt` seen, in millis. `undefined` means no snapshot yet. */
	const lastSeenRef = useRef<number | null | undefined>(undefined);

	useEffect(() => {
		if (!fireStore || !user?.id) {
			return;
		}

		// A different account in the same tab starts its own baseline.
		lastSeenRef.current = undefined;

		const documentRef = doc(fireStore, "vendors", user.id);

		const unsubscribe = onSnapshot(documentRef, (snapshot) => {
			if (!snapshot.exists()) {
				return;
			}

			const updatedAt = snapshot.data().updatedAt as Timestamp | undefined;
			// A pending server timestamp arrives as null on the local echo.
			const updatedAtMs = updatedAt?.toMillis?.() ?? null;

			// First delivery is the document as it already stands, which whatever
			// mounted this hook has just loaded anyway.
			if (lastSeenRef.current === undefined) {
				lastSeenRef.current = updatedAtMs;
				return;
			}

			if (updatedAtMs === lastSeenRef.current) {
				return;
			}

			lastSeenRef.current = updatedAtMs;
			callbackRef.current();
		});

		return () => unsubscribe();
	}, [user?.id]);
};

export default useOnVendorUpdated;
